import { useCallback, useEffect, useState } from 'react'
import type { WatcherDetail, WatcherRecord } from '@shared/watch'
import { subscribeUserEvents, watchersApi } from '../lib/api'

/** One watcher / scan, kept live: its readings every few seconds while it is checking, and on every change. */
export function useWatcher(id: string | null): { watcher: WatcherDetail | null; error: string | null; reload: () => Promise<void> } {
  const [watcher, setWatcher] = useState<WatcherDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    if (!id) return
    try {
      setWatcher(await watchersApi.get(id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the watcher')
    }
  }, [id])
  useEffect(() => {
    if (!id) return
    void reload()
    const off = subscribeUserEvents((e) => {
      if (e.type === 'watchers') void reload()
    })
    return off
  }, [id, reload])
  // Live numbers while it is active (the engine judges every 2 s).
  const live = watcher?.status === 'active'
  useEffect(() => {
    if (!live) return
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void reload()
    }, 4000)
    return () => clearInterval(t)
  }, [live, reload])
  return { watcher, error, reload }
}

/** All watchers and scans, kept live. */
export function useWatchers(): { watchers: WatcherRecord[] | null; error: string | null; reload: () => Promise<void> } {
  const [watchers, setWatchers] = useState<WatcherRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    try {
      setWatchers(await watchersApi.list())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load watchers')
    }
  }, [])
  useEffect(() => {
    void reload()
    let t: ReturnType<typeof setTimeout> | null = null
    const off = subscribeUserEvents((e) => {
      if (e.type !== 'watchers') return
      if (t) clearTimeout(t)
      t = setTimeout(() => void reload(), 300)
    })
    return () => {
      off()
      if (t) clearTimeout(t)
    }
  }, [reload])
  return { watchers, error, reload }
}
